import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

import {
    setChonkyDefaults,
    ChonkyActions,
    FileHelper,
    FileBrowser, 
    FileNavbar,
    FileToolbar,
    FileList,
    defineFileAction,
    ChonkyIconName,
} from 'chonky';
import { ChonkyIconFA } from 'chonky-icon-fontawesome';

import { broncoURL, commitHash } from 'Vulture/ENV';
import { getSession } from 'Vulture/Session';

import CreateFolderModal from './CreateFolderModal.js';
import CreateFileModal from './CreateFileModal.js';
import EditFileModal from './EditFileModal.js';
import EditFolderModal from './EditFolderModal.js';
import { transformFolderData, transformFileData } from '../../../Util/ChonkyFileBrowserETL';

import style from './ChonkyFileBrowser.module.css';

setChonkyDefaults({ iconComponent: ChonkyIconFA });

const initialFileMap = {
    root: {
        id: 'root',
        name: 'Home',
        isDir: true,
        childrenIds: [],
        parentId: null,
    },
};

function ChonkyFileBrowser(props) {
    const [fileMap, setFileMap] = useState(initialFileMap);
    const [currentFolderId, setCurrentFolderId] = useState('root');
    const fileBrowserRef = useRef(null);
    const wrapperRef = useRef(null);
    const fileMapRef = useRef(fileMap);

    useEffect(() => {
        fileMapRef.current = fileMap;
    }, [fileMap]);

    const getAccountsData = useCallback(() => {
        props.app.setState({ loading: true }); 
        const session = getSession();
        const url = `${broncoURL}/api/folder`;
        
        const headers = {
            headers: {
                'X-Auth-Session': session.session,
                'X-Spark-API-version': commitHash,
            },
        };
    
        axios.get(url, headers)
            .then((res) => {
                if (res.status !== 200) {
                    throw new Error(`Invalid response from ${broncoURL}/api/folder`);
                }
                return res.data;
            })
            .then((folderData) => {
                setFileMap(transformFolderData(folderData));
                props.app.setState({ loading: false });
            })
            .catch((err) => {
                console.error('Folder fetch error:', {
                    error: err.message,
                    response: err.response?.data
                });
                alert(`Failed to load folders: ${err.response?.data?.message || err.message}`);
                setFileMap(initialFileMap); 
                props.app.setState({ loading: false });
            });
    }, []);

    const openCreateFolderModal = () => {
        props.app.openModal({
            modalSize:  1,
            showModal:  true,
            modalProps: {
                title: 'Add New Folder',
                jsx:   (
                    <CreateFolderModal 
                        currentFolderId={currentFolderId}
                        refreshFileBrowserData={refreshFileBrowserData}
                        app={props.app}
                    />
                ),
            },
        });
    }

    const openCreateFileModal = () => {
        props.app.openModal({
            modalSize:  1,
            showModal:  true,
            modalProps: {
                title: 'Upload New File',
                jsx:   (
                    <CreateFileModal 
                        currentFolderId={currentFolderId}
                        refreshFileBrowserData={refreshFileBrowserData}
                        app={props.app}
                    />
                ),
            },
        });
    }

    const openEditModal = () => {
        const selectedFileIds = Array.from(fileBrowserRef.current.getFileSelection());
        const selectedFiles = selectedFileIds.map(id => fileMapRef.current[id]).filter(Boolean);
    
        if (selectedFiles.length !== 1) {
            alert('Please select exactly one item to edit');
            return;
        }
    
        const selectedItem = selectedFiles[0];
    
        if (selectedItem.id.startsWith('file_')) {
            props.app.openModal({
                modalSize: 1,
                showModal: true,
                modalProps: {
                    title: 'Edit File',
                    jsx: (
                        <EditFileModal
                            file={selectedItem}
                            refreshFileBrowserData={refreshFileBrowserData}
                            app={props.app}
                        />
                    ),
                },
            });
        } else if (selectedItem.id.startsWith('node_')) {
            props.app.openModal({
                modalSize: 1,
                showModal: true,
                modalProps: {
                    title: 'Edit Folder',
                    jsx: (
                        <EditFolderModal
                            folder={selectedItem}
                            refreshFileBrowserData={refreshFileBrowserData}
                            app={props.app}
                        />
                    ),
                },
            });
        }
    };

    const refreshFileBrowserData = useCallback(async () => {
        props.app.setState({ loading: true }); 
        try {
            const session = getSession();
            const folderUrl = `${broncoURL}/api/folder`;
            const headers = {
                headers: {
                    'X-Auth-Session': session.session,
                    'X-Spark-API-version': commitHash,
                },
            };
    
            const folderResponse = await axios.get(folderUrl, headers);
            if (folderResponse.status !== 200) {
                throw new Error(`Invalid response from ${folderUrl}`);
            }
            
            const newFileMap = transformFolderData(folderResponse.data);
            
            if (currentFolderId !== 'root') {
                const backendFolderId = currentFolderId.replace('node_', '');
                const filesUrl = `${broncoURL}/api/folder/${backendFolderId}/file`;
                const filesResponse = await axios.get(filesUrl, headers);
                
                if (filesResponse.status === 200) {
                    setFileMap(transformFileData(newFileMap, currentFolderId, filesResponse.data.files));
                    return;
                }
            }
            
            setFileMap(newFileMap);
        } catch (err) {
            console.error('Refresh error:', err);
            alert(`Failed to refresh data: ${err.response?.data?.message || err.message}`);
        } finally {
            props.app.setState({ loading: false });
        }
    }, [currentFolderId]);
    
    useEffect(() => {
        if (currentFolderId === 'root') return;
    
        const folder = fileMapRef.current[currentFolderId];
        if (!folder) return;
    
        const hasFiles = folder.childrenIds.some(id => id.startsWith('file_'));
        if (hasFiles) return;
    
        props.app.setState({ loading: true });
        const backendFolderId = currentFolderId.replace('node_', '');
        const session = getSession();
        const url = `${broncoURL}/api/folder/${backendFolderId}/file`;
        const headers = {
            headers: {
                'X-Auth-Session': session.session,
                'X-Spark-API-version': commitHash,
            },
        };
    
        axios.get(url, headers)
        .then(res => {
            props.app.setState({ loading: false });
            if (res.status !== 200) throw new Error(`Invalid response from ${url}`);
            return res.data.files; 
        })
        .then(filesArray => {
            setFileMap(prevMap => 
                transformFileData(prevMap, currentFolderId, filesArray)
            );
        })
        .catch(err => {
            console.error('File fetch error:', err);
            alert(`Failed to load files: ${err.response?.data?.message || err.message}`);
            props.app.setState({ loading: false });
        });
    }, [currentFolderId]);

    useEffect(() => {
        getAccountsData();
    }, [getAccountsData]);

    const files = useMemo(() => {
        const currentFolder = fileMap[currentFolderId];
        return currentFolder.childrenIds.map(id => fileMap[id]);
    }, [currentFolderId, fileMap]);

    const filesSortedByNames = useMemo(() => {
        return [...files].sort((a, b) => {
            if (a.isDir === b.isDir) {
                const nameA = a.name || '';
                const nameB = b.name || '';
                return nameA.localeCompare(nameB);
            }
            if (a.isDir) return -1;
            return 1;
        });
    }, [files]);

    const folderChain = useMemo(() => {
        const chain = [];
        let currentId = currentFolderId;

        while (currentId) {
            const folder = fileMap[currentId];
            chain.unshift(folder);
            currentId = folder.parentId;
        }

        return chain;
    }, [currentFolderId, fileMap]);

    const handleFileAction = useCallback((data) => {
        switch (data.id) {
            case ChonkyActions.DeleteFiles.id: {
                const confirmed = window.confirm(
                    'Are you sure you want to delete the selected items? This action cannot be undone.'
                );
                if (!confirmed) return;
                const selectedFileIds = Array.from(fileBrowserRef.current.getFileSelection());
                const selectedFiles = selectedFileIds
                    .map(id => fileMapRef.current[id])
                    .filter(Boolean); // Filter out undefined files
    
                const filesToDelete = selectedFiles.filter(file => file.id.startsWith('file_'));
                const foldersToDelete = selectedFiles.filter(file => file.id.startsWith('node_'));
    
                const fileIds = filesToDelete.map(file => file.id.replace('file_', ''));
                const folderIds = foldersToDelete.map(folder => folder.id.replace('node_', ''));
    
                if (fileIds.length + folderIds.length === 0) return;
    
                const session = getSession();
                const headers = {
                    headers: {
                        'X-Auth-Session': session.session,
                        'X-Spark-API-version': commitHash,
                    }
                };
    
                const deletePromises = [
                    ...fileIds.map(id => axios.delete(`${broncoURL}/api/file/${id}`, headers)),
                    ...folderIds.map(id => axios.delete(`${broncoURL}/api/folder/${id}`, headers))
                ];
    
                props.app.setState({ loading: true });
    
                Promise.all(deletePromises)
                    .then(() => refreshFileBrowserData())
                    .catch(error => {
                        console.error('Delete error:', {
                            error: error.message,
                            response: error.response?.data
                        });
                        const message = error.response?.data?.message || error.message;
                        alert(`Deletion failed: ${message}`);
                    })
                    .finally(() => props.app.setState({ loading: false }));
                break;
            }
            case ChonkyActions.OpenFiles.id: {
                const { targetFile, files } = data.payload;
                const fileToOpen = targetFile ?? files[0];
                if (fileToOpen && FileHelper.isDirectory(fileToOpen)) {
                    setCurrentFolderId(fileToOpen.id);
                } else {
                    openEditModal();
                }
                break;
            }
            case ChonkyActions.EndDragNDrop.id: {
                const { draggedFile, destination, selectedFiles } = data.payload;
    
                const filesToMove = selectedFiles.length > 0 ? selectedFiles : [draggedFile];
                if (!filesToMove.length || !destination || !destination.isDir) {
                    alert('Invalid move operation');
                    return;
                }
    
                // Convert Chonky IDs to backend IDs
                const sourceFileIds = filesToMove.map(file => 
                    file.id.startsWith('file_') ? file.id.slice(5) : file.id
                );
                const destFolderId = destination.id.startsWith('node_') 
                    ? destination.id.slice(5) 
                    : destination.id;
    
                const session = getSession();
    
                props.app.setState({ loading: true });
                axios.post(`${broncoURL}/api/file/move`, {
                    source_file_ids: sourceFileIds,
                    dest_folder_id: destFolderId
                }, {
                    headers: {
                        'X-Auth-Session': session.session,
                        'X-Spark-API-version': commitHash
                    }
                })
                .then(response => {
                    if (response.status === 200) {
                        refreshFileBrowserData();
                    } else {
                        throw new Error('Failed to move files');
                    }
                })
                .catch(error => {
                    console.error('Move error:', error);
                    const message = error.response?.data?.message || error.message;
                    alert(`File move failed: ${message}`);
                })
                .finally(() => props.app.setState({ loading: false }));
                break;
            }
            case 'custom_folder_upload': {
                openCreateFolderModal();
                break;
            }
            case 'custom_edit': {
                openEditModal();
                break;
            }
            case 'custom_file_upload': {
                openCreateFileModal();
                break;
            }
            default:
                break;
        }
    }, [refreshFileBrowserData]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            event.preventDefault();

            const currentSelection = fileBrowserRef.current.getFileSelection();
            const selectedFileId = currentSelection.values().next().value;
            const currentIndex = filesSortedByNames.findIndex(file => file?.id === selectedFileId);
            const newSelection = new Set();

            switch (event.key) {
                case 'ArrowUp':
                    break;
                case 'ArrowDown':
                    console.log('Arrow Down Pressed');
                    break;
                case 'ArrowLeft':
                    const previousIndex = currentIndex > 0 ? currentIndex - 1 : currentIndex;
                    const previousFile = filesSortedByNames[previousIndex];
            
                    if (previousFile) {
                        newSelection.add(previousFile.id);
                        fileBrowserRef.current.setFileSelection(newSelection);
                    }
                    break;
                case 'ArrowRight':
                    const nextIndex = currentIndex < filesSortedByNames.length - 1 ? currentIndex + 1 : currentIndex;
                    const nextFile = filesSortedByNames[nextIndex];
            
                    if (nextFile) {
                        newSelection.add(nextFile.id);
                        fileBrowserRef.current.setFileSelection(newSelection);
                    }
                    break;
                case 'Enter':
                    if (selectedFileId) {
                        const selectedFile = filesSortedByNames.find(f => f.id === selectedFileId);
                        if (selectedFile && selectedFile.isDir) {
                            setCurrentFolderId(selectedFile.id);
                        }
                    }
                    break;
                default:
                    break;
            }
        };

        const wrapperElement = wrapperRef.current;
        wrapperElement.addEventListener('keydown', handleKeyDown);
        wrapperElement.focus();

        return () => {
            wrapperElement.removeEventListener('keydown', handleKeyDown);
        };
    }, [filesSortedByNames]);

    const CustomDeleteAction = defineFileAction({
        id: ChonkyActions.DeleteFiles.id,
        button: {
            name: 'Delete', 
            toolbar: true,
            contextMenu: true,
            icon: ChonkyIconName.trash
        },
    });

    const CustomFolderUploadAction = defineFileAction({
        id: 'custom_folder_upload',
        button: {
            name: 'New folder', 
            toolbar: true,
            contextMenu: true,
            icon: ChonkyIconName.folderCreate
        },
    });

    const CustomFileUploadAction = defineFileAction({
        id: 'custom_file_upload',
        button: {
            name: 'Upload file', 
            toolbar: true,
            contextMenu: true,
            icon: ChonkyIconName.file
        },
    });

    const CustomEditAction = defineFileAction({
        id: 'custom_edit',
        button: {
            name: 'Edit', 
            toolbar: true,
            contextMenu: true,
        },
    });

    let ChonkyToolbarActions = [currentFolderId !== 'root' && CustomFileUploadAction, CustomDeleteAction, CustomFolderUploadAction, CustomEditAction];

    return (
        <>   
            <div 
                ref={wrapperRef}
                tabIndex={-1}
                style={{ height: 600, outline: 'none' }}
                className={style.fileBrowserWrapper}
            >
                <FileBrowser
                    ref={fileBrowserRef}
                    files={filesSortedByNames}
                    folderChain={folderChain}
                    onFileAction={handleFileAction}
                    fileActions={ChonkyToolbarActions}
                >
                    <FileNavbar />
                    <FileToolbar />
                    <FileList />    
                </FileBrowser>
            </div>
        </>
    );
}

export default ChonkyFileBrowser;